<template>
  <v-app
    v-if="videoUrl"
    data-video-card
    data-qa-is-lpu
    class="d-inline-flex"
  >
    <v-sheet color="transparent">
      <div
        data-qa="video_card_play_icon"
        class="video-card-icon"
        @click="handleOpenDialog"
      >
        <v-img
          v-ripple
          :alt="$t('Открыть видеовизитку', { ns: $i18next.language })"
          width="28"
          height="28"
          src="/static/_v1/pd/icons/ui-kit/video-card-play.svg"
        />
      </div>
    </v-sheet>
    <v-dialog
      v-if="isMobile"
      v-model="isDialogVisible"
      data-qa="video_card_dialog"
      fullscreen
      divider
    >
      <div
        class="video-card-wrapper text"
        data-qa="video_card_dialog"
      >
        <div
          class="video-card-player-close ui-icon_reset bg-gray-0--text ui-icon-close-not-a-circle"
          @click="isDialogVisible = false"
        />
        <div
          class="video-card-switch-state"
          @click="videoSwitchState"
        />
        <div
          v-if="!isVideoPlaying"
          class="video-card-play"
        >
          <div class="ui-icon_reset primary--text ui-icon-player-play" />
        </div>
        <div
          v-if="videoTimeTotal && videoTimeCurrent"
          class="video-card-time-cover"
        >
          <div class="video-card-time">
            <div
              class="text-body-1 bg-gray-0--text"
            >
              {{ videoTimeCurrent }}
            </div>
            <span class="text-body-1 bg-gray-60--text px-1">/</span>
            <div
              class="text-body-1 bg-gray-60--text"
            >
              {{ videoTimeTotal }}
            </div>
          </div>
        </div>
        <video
          ref="videoRef"
          data-qa="video_card_video"
          playsinline
          class="video-card-player"
          :src="videoUrl"
          @timeupdate="handleTimeupdate"
          @loadedmetadata="handleLoadedmetadata"
          @ended="handleEnded"
        />
      </div>
    </v-dialog>
    <CoreDialog
      v-else
      v-model="isDialogVisible"
      data-qa="video_card_dialog"
      size="lg"
      :title="$t('Видеовизитка', { ns: $i18next.language })"
      divider
    >
      <template #prependBtn="{ btnProps }">
        <v-btn
          v-bind="btnProps"
          @click="isDialogVisible = false"
        >
          <span class="ui-icon-close-not-a-circle" />
        </v-btn>
      </template>
      <video
        ref="videoRef"
        data-qa="video_card_video"
        controls
        class="video-card-player"
        :src="videoUrl"
        @ended="handleEnded"
      />
    </CoreDialog>
  </v-app>
</template>

<script lang="ts" setup>
import {
  ref, nextTick, onMounted, watch,
} from 'vue'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import YandexGoals from 'www/modules/YandexGoals'
import writeQuantityViews from 'www/vue_components/common/VideoCardLpu/api/writeQuantityViews'
import { dispatchCustomEventGlobally } from 'www/utils'

const props = defineProps<{
  videoUrl: string
  videoUuid: string
  isLpu?: boolean
}>()

const timeoutId = ref<ReturnType<typeof setTimeout>>()
const videoTimeTotal = ref('0')
const videoTimeCurrent = ref('0')
const isVideoPlaying = ref(false)
const isMobile = ref(window.MOBILE_VERSION)
const isDialogVisible = ref(false)
const yaGoals = new YandexGoals()

const videoRef = ref()

function sendYandexGoal(name: string) {
  yaGoals.send({ name })
}

function handleCloseDialog() {
  isDialogVisible.value = false
  isVideoPlaying.value = true

  videoSwitchState()

  nextTick(() => {
    if (videoRef.value) {
      const diff = videoRef.value.duration - videoRef.value.currentTime

      if (diff !== 0) { // значит видео НЕ досмотрено до конца
        sendYandexGoal('common_LpuPage_VideoCard__closeWithoutView')
      }

      videoRef.value.currentTime = 0

      dispatchCustomEventGlobally('aside-sizes:update')
    }
  })

  // Костыль, который снижает z-index кнопке. Иначе она может перекрыть собой иные открытые окна.
  // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
  timeoutId.value = setTimeout(() => { // Задержка нужна для десктопной версии, чтобы поиск не мигал при закрытии модального окна с видео
    document.querySelectorAll<HTMLDivElement>('.video-card-vue')
      .forEach(node => { node.style.zIndex = '5' })
  }, 500)
}

async function handleOpenDialog() {
  isDialogVisible.value = true

  if (timeoutId.value) { // Если пользователь быстро закроет и откроет окно - z-index будет некорректным из-за setTimeout выше. Поэтому исполнение сброса z-index необходимо обнулить
    clearTimeout(timeoutId.value)
  }

  // Костыль, который повышает z-index кнопке.
  // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
  document.querySelectorAll<HTMLDivElement>('.video-card-vue')
    .forEach(node => { node.style.zIndex = '100' })

  videoSwitchState()
  sendYandexGoal('common_LpuPage_VideoCard__play')

  try {
    if (!props.videoUuid) {
      return
    }

    await writeQuantityViews(props.videoUuid)
  } catch { /* Empty */ }
}

onMounted(() => {
  window.addEventListener('click-video-preview-image', handleOpenDialog)
})

watch(isDialogVisible, value => {
  if (!value) {
    handleCloseDialog()
  }
})

function videoSwitchState() {
  nextTick(() => {
    if (!videoRef.value) {
      return
    }

    if (isVideoPlaying.value) {
      isVideoPlaying.value = false
      videoRef.value.pause()
    } else {
      isVideoPlaying.value = true
      videoRef.value.play()
    }
  })
}

function handleTimeupdate() {
  videoTimeCurrent.value = convertSeconds(videoRef.value.currentTime)
}

function handleLoadedmetadata() {
  videoTimeTotal.value = convertSeconds(videoRef.value.duration)
  videoTimeCurrent.value = convertSeconds(videoRef.value.currentTime)
}

function handleEnded() {
  sendYandexGoal('common_LpuPage_VideoCard__finish')
}

function convertSeconds(sec: string | number) {
  const date = new Date(1970, 0, 0, 0, 0, Number(sec) || 0)
    .toLocaleTimeString('ru')
    .split(':')

  return `${Number(date[1])}:${date[2]}`
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/mixins';

#app[data-video-card] {
  @include v-app-reset;
}

.height-inherit {
  height: inherit;
}

.video-card {
  &-wrapper {
    position: relative;
  }

  &-icon {
    background-color: $ui-kit-bg-gray-0;
    padding: 2px;
    border-radius: $border-radius-round;
  }

  &-player {
    width: 100%;
    height: 100vh;
    display: block;

    @supports (height: 100dvh) {
      height: 100dvh;
    }
  }

  &-play {
    z-index: 1;
    width: 40px;
    height: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: calc(50% - 20px);
    left: calc(50% - 20px);
    background-color: $ui-kit-bg-primary;
    border-radius: $border-radius-circle;
  }

  &-player-close {
    top: 16px;
    left: 8px;
    z-index: 5;
    position: absolute;
  }

  &-switch-state {
    width: 100%;
    left: 0;
    bottom: 0;
    z-index: 5;
    position: absolute;
    height: calc(100% - 50px);
  }

  &-time-cover {
    width: 100%;
    left: 0;
    bottom: 100px;
    position: absolute;
    display: flex;
    justify-content: center;
  }

  &-time {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: $border-radius-md;
    background: rgba($ui-kit-text, 0.8);
  }
}

[data-flavour='full'] {
  .video-card-player {
    height: 100%;
  }
}

@media screen and (min-height: 900px) {
  [data-flavour='full'] {
    .video-card-player {
      max-height: 743px;
    }
  }
}
</style>

<style lang="scss">
#app[data-video-card] .v-dialog--fullscreen {
  overflow-y: hidden;
}

#app[data-video-card] .v-dialog:not(.v-dialog--fullscreen) {
  max-height: initial;
  height: 85%;
}

@media screen and (min-height: 900px) {
  [data-flavour='full'] {
    #app[data-video-card] .v-dialog:not(.v-dialog--fullscreen) {
      height: auto;
    }
  }
}
</style>
